import { useState, useEffect, useCallback, useMemo } from "react";
import Tooltip from "../Tooltip";
import { getAdminIdFromStoreFrontId } from "../../../helpers/shopify";
import { IGVariant, TTimeFilter } from "../../../types";
import { useTrackContext } from "../../../hooks/useTrackContext";
import Notifier from "../Notify";
import { sortBySku } from "../../../helpers/utils";
import { dispatchCartUpdates, handleAddToCartClick, updateCartQuantity } from "../../../utils/cartOperations";
import getCartData from "../../../utils/getCartData";
import BattleRaceIcon from "../../battleKart/SVGIcons/BattleRaceIcon";
import BattleColourIcon from "../../battleKart/SVGIcons/BattleColourIcon";
import BattleKartFootSVGIcon from "../../battleKart/SVGIcons/BattleKartFootSVGIcon";
import BattleSnakeIcon from "../../battleKart/SVGIcons/BattleSnakeIcon";

interface IPropsVariantBlockRace {
  variant: IGVariant;
  getRaceTimeForVariant: (variant: IGVariant) => string;
  selectedVariants: IGVariant[];
  onClick?: () => void;
  activeRace?: IGVariant | null;
  formattedDateString: Date;
  sessionDisplaytime: TTimeFilter;
}

export function VariantsBlock({ variant, sessionDisplaytime, getRaceTimeForVariant, selectedVariants, onClick, activeRace, formattedDateString }: IPropsVariantBlockRace) {
  const isSelected = useMemo(() => selectedVariants.find((v) => v.id === variant.id), [selectedVariants, variant.id]);
  const isActiveRace = activeRace?.id === variant.id;
  const leftSeats = variant?.quantityAvailable;
  const {
    dispatch,
    mainTrackSelection,
    miniTrackSection,
    vrSelection,
    juniorTrackSelection,
    trackSelectionOption,
    battleKartSelection,
    productRenderedLocation,
    headlessCartInfo,
    headlessCart,
  } = useTrackContext();

  const currentQuantity: number = useMemo(() => {
    if (trackSelectionOption.title === "MAIN_TRACK") {
      return mainTrackSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      return miniTrackSection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
      return juniorTrackSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "VIRTUAL_REALITY") {
      return vrSelection.playersandsessions.numberOfPlayers;
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      return battleKartSelection.racesSeats.numberOfSeats;
    }
    return 0;
  }, [
    trackSelectionOption.title,
    mainTrackSelection.racesSeats.numberOfSeats,
    miniTrackSection.racesSeats.numberOfSeats,
    juniorTrackSelection.racesSeats.numberOfSeats,
    vrSelection.playersandsessions.numberOfPlayers,
    battleKartSelection.racesSeats.numberOfSeats,
  ]);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const handleQuantityUpdate = useCallback(
    async (id: string, newQuantity: number) => {
      try {
        const result = await updateCartQuantity(id, newQuantity, productRenderedLocation, headlessCartInfo);
        dispatchCartUpdates(dispatch, result.cart, productRenderedLocation);
      } catch (error) {
        console.error("Failed to update cart quantity:", error);
      }
    },
    [productRenderedLocation, headlessCartInfo, dispatch]
  );

  useEffect(() => {
    if (mainTrackSelection.variantsAndDate.selectedVariants.length === 0) {
      dispatch({
        type: "SET_DATE_AND_VARIANT",
        payload: {
          variantsAndDate: {
            selectedSessionTime: null,
            selectedDate: new Date(),
            selectedVariants: [],
            selectedVariantsForUpsell: [],
          },
        },
      });
    }
    if (miniTrackSection.variantsAndDate.selectedVariants.length === 0) {
      dispatch({
        type: "SET_DATE_AND_VARIANT_MINI_TRACK",
        payload: {
          variantsAndDate: {
            selectedSessionTime: null,
            selectedDate: new Date(),
            selectedVariants: [],
            selectedVariantsForUpsell: [],
          },
        },
      });
    }
    if (vrSelection.variantsAndDate.selectedVariants.length === 0) {
      dispatch({
        type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
        payload: {
          variantsAndDate: {
            selectedSessionTime: null,
            selectedDate: new Date(),
            selectedVariants: [],
            selectedVariantsForUpsell: [],
          },
        },
      });
    }
  }, [
    dispatch,
    mainTrackSelection.variantsAndDate.selectedVariants.length,
    miniTrackSection.variantsAndDate.selectedVariants.length,
    vrSelection.variantsAndDate.selectedVariants.length,
  ]);

  const checkAvailability = () => {
    // checking for infinity is for product selector component
    switch (trackSelectionOption.title) {
      case "MAIN_TRACK":
        return variant?.availableForSale && (mainTrackSelection.racesSeats.numberOfSeats <= variant?.quantityAvailable || mainTrackSelection.racesSeats.numberOfSeats === Infinity);
      case "MINI_TRACK":
        return variant?.availableForSale && (miniTrackSection.racesSeats.numberOfSeats <= variant?.quantityAvailable || miniTrackSection.racesSeats.numberOfSeats === Infinity);
      case "JUNIOR_TRACK":
        return (
          variant?.availableForSale && (juniorTrackSelection.racesSeats.numberOfSeats <= variant?.quantityAvailable || juniorTrackSelection.racesSeats.numberOfSeats === Infinity)
        );
      case "BATTLE_KART_TRACK":
        return (
          variant?.availableForSale && (battleKartSelection.racesSeats.numberOfSeats <= variant?.quantityAvailable || battleKartSelection.racesSeats.numberOfSeats === Infinity)
        );
      default:
        return (
          variant?.availableForSale && (vrSelection.playersandsessions.numberOfPlayers <= variant.quantityAvailable || vrSelection.playersandsessions.numberOfPlayers === Infinity)
        );
    }
  };

  const isAvailable = checkAvailability();

  const getGameTimeForVariant = useCallback((variant: IGVariant) => variant.selectedOptions.find((e) => e.name.includes("Time"))?.value || "", []);

  const handlePriceColor = (xPrice: string) => {
    const xNumber = parseFloat(xPrice);
    if (trackSelectionOption.title === "MAIN_TRACK" || trackSelectionOption.title === "JUNIOR_TRACK" || trackSelectionOption.title === "VIRTUAL_REALITY") {
      if (xNumber <= 15) {
        return "tw-bg-[#C03FFF]";
      } else if (xNumber <= 19) {
        return "tw-bg-[#C03FFF]";
      } else if (xNumber <= 24) {
        return "tw-bg-[#8A75FF]";
      } else if (xNumber <= 29) {
        return "tw-bg-[#8A75FF]";
      } else if (xNumber <= 39) {
        return "tw-bg-[#C03FFF]";
      } else if (xNumber <= 49) {
        return "tw-bg-[#E632E5]";
      } else if (xNumber <= 59) {
        return "tw-bg-[#510EA8]";
      } else if (xNumber <= 69) {
        return "tw-bg-[#510EA8]";
      }
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      if (xNumber <= 9) {
        return "tw-bg-[#C03FFF]";
      } else if (xNumber <= 14) {
        return "tw-bg-[#C03FFF]";
      } else if (xNumber <= 15) {
        return "tw-bg-[#8A75FF]";
      } else if (xNumber <= 19) {
        return "tw-bg-[#C03FFF]";
      } else if (xNumber <= 24) {
        return "tw-bg-[#E632E5]";
      } else if (xNumber <= 29) {
        return "tw-bg-[#510EA8]";
      }
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      // Battle Kart uses orange color scheme
      return "tw-bg-[#EA581A]";
    }
  };

  const maxSelectableVariants =
    trackSelectionOption.title === "MAIN_TRACK"
      ? mainTrackSelection.racesSeats.numberOfRaces
      : trackSelectionOption.title === "MINI_TRACK"
      ? miniTrackSection.racesSeats.numberOfRaces
      : trackSelectionOption.title === "JUNIOR_TRACK"
      ? juniorTrackSelection.racesSeats.numberOfRaces
      : trackSelectionOption.title === "BATTLE_KART_TRACK"
      ? battleKartSelection.racesSeats.numberOfRaces
      : vrSelection.playersandsessions.numberOfSessions;

  const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAvailable) {
      if (onClick) {
        const cartPayload = {
          variantSelectedTime: new Date(formattedDateString),
          variantsSelectedObject: variant,
        };

        if (trackSelectionOption.title === "MAIN_TRACK") {
          if (isSelected) {
            dispatch({
              type: "SET_DATE_AND_VARIANT",
              payload: {
                variantsAndDate: {
                  ...mainTrackSelection.variantsAndDate,
                  selectedSessionTime: sessionDisplaytime,
                  selectedDate: new Date(formattedDateString),
                  selectedVariants: sortBySku(
                    mainTrackSelection.variantsAndDate.selectedVariants.filter((xv) => xv.variantsSelectedObject.id !== variant.id),
                    trackSelectionOption.title
                  ),
                },
              },
            });
            onClick();
            const cartData = await getCartData(productRenderedLocation, headlessCartInfo);

            let lineItems: any[] = [];
            if (cartData) {
              const { cart } = cartData;
              if (cart) {
                const { lines, items } = cart;
                if (items && items.length > 0) {
                  lineItems = items;
                }
                if (lines) {
                  const { edges } = lines;
                  if (edges) {
                    lineItems = edges.map((edge: any) => ({ ...edge.node.merchandise, headlessCartId: edge.node.id }));
                  }
                }
              }
            }

            const lineItem = lineItems.find((line) => variant.id.includes(line.id));

            if (lineItem) {
              await handleQuantityUpdate(productRenderedLocation === "external" ? lineItem.headlessCartId : lineItem.id, 0);
            }
          } else if (selectedVariants.length + 1 <= maxSelectableVariants) {
            const addSelectedVariants = {
              ...mainTrackSelection.variantsAndDate,
              selectedSessionTime: sessionDisplaytime,
              selectedDate: new Date(formattedDateString),
              selectedVariants: sortBySku(
                [
                  ...mainTrackSelection.variantsAndDate.selectedVariants,
                  {
                    variantSelectedTime: new Date(formattedDateString),
                    variantsSelectedObject: variant,
                  },
                ],
                trackSelectionOption.title
              ),
            };
            dispatch({
              type: "SET_DATE_AND_VARIANT",
              payload: {
                variantsAndDate: addSelectedVariants,
              },
            });
            onClick();
            await handleAddToCartClick([cartPayload], currentQuantity, productRenderedLocation, headlessCart, headlessCartInfo, dispatch, false, maxSelectableVariants);
          } else {
            dispatch({
              type: "SET_NOTIFICATION",
              payload: {
                title: `Too many sessions selected.`,
                content: `You have selected the maximum number of timeslots for your booking.`,
                isVisible: true,
                variant: {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              },
            });
          }
        } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
          if (isSelected) {
            dispatch({
              type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
              payload: {
                variantsAndDate: {
                  ...juniorTrackSelection.variantsAndDate,
                  selectedSessionTime: sessionDisplaytime,
                  selectedDate: new Date(formattedDateString),
                  selectedVariants: juniorTrackSelection.variantsAndDate.selectedVariants.filter((xv) => xv.variantsSelectedObject.id !== variant.id),
                },
              },
            });
            onClick();
            const cartData = await getCartData(productRenderedLocation, headlessCartInfo);

            let lineItems: any[] = [];
            if (cartData) {
              const { cart } = cartData;
              if (cart) {
                const { lines, items } = cart;
                if (items && items.length > 0) {
                  lineItems = items;
                }
                if (lines) {
                  const { edges } = lines;
                  if (edges) {
                    lineItems = edges.map((edge: any) => ({ ...edge.node.merchandise, headlessCartId: edge.node.id }));
                  }
                }
              }
            }

            const lineItem = lineItems.find((line) => variant.id.includes(line.id));

            if (lineItem) {
              await handleQuantityUpdate(productRenderedLocation === "external" ? lineItem.headlessCartId : lineItem.id, 0);
            }
          } else if (selectedVariants.length + 1 <= maxSelectableVariants) {
            const addSelectedVariants = {
              ...juniorTrackSelection.variantsAndDate,
              selectedSessionTime: sessionDisplaytime,
              selectedDate: new Date(formattedDateString),
              selectedVariants: [
                ...juniorTrackSelection.variantsAndDate.selectedVariants,
                {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              ],
            };

            dispatch({
              type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
              payload: {
                variantsAndDate: addSelectedVariants,
              },
            });
            onClick();
            await handleAddToCartClick([cartPayload], currentQuantity, productRenderedLocation, headlessCart, headlessCartInfo, dispatch, false, maxSelectableVariants);
          } else {
            dispatch({
              type: "SET_NOTIFICATION",
              payload: {
                title: `Too many sessions selected.`,
                content: `You have selected the maximum number of timeslots for your booking.`,
                isVisible: true,
                variant: {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              },
            });
          }
        } else if (trackSelectionOption.title === "MINI_TRACK") {
          if (isSelected) {
            dispatch({
              type: "SET_DATE_AND_VARIANT_MINI_TRACK",
              payload: {
                variantsAndDate: {
                  ...miniTrackSection.variantsAndDate,
                  selectedSessionTime: sessionDisplaytime,
                  selectedDate: new Date(formattedDateString),
                  selectedVariants: miniTrackSection.variantsAndDate.selectedVariants.filter((xv) => xv.variantsSelectedObject.id !== variant.id),
                },
              },
            });
            onClick();
            const cartData = await getCartData(productRenderedLocation, headlessCartInfo);

            let lineItems: any[] = [];
            if (cartData) {
              const { cart } = cartData;
              if (cart) {
                const { lines, items } = cart;
                if (items && items.length > 0) {
                  lineItems = items;
                }
                if (lines) {
                  const { edges } = lines;
                  if (edges) {
                    lineItems = edges.map((edge: any) => ({ ...edge.node.merchandise, headlessCartId: edge.node.id }));
                  }
                }
              }
            }

            const lineItem = lineItems.find((line) => variant.id.includes(line.id));

            if (lineItem) {
              await handleQuantityUpdate(productRenderedLocation === "external" ? lineItem.headlessCartId : lineItem.id, 0);
            }
          } else if (selectedVariants.length + 1 <= maxSelectableVariants) {
            const addSelectedVariants = {
              ...miniTrackSection.variantsAndDate,
              selectedSessionTime: sessionDisplaytime,
              selectedDate: new Date(formattedDateString),
              selectedVariants: [
                ...miniTrackSection.variantsAndDate.selectedVariants,
                {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              ],
            };
            dispatch({
              type: "SET_DATE_AND_VARIANT_MINI_TRACK",
              payload: {
                variantsAndDate: addSelectedVariants,
              },
            });
            onClick();
            await handleAddToCartClick([cartPayload], currentQuantity, productRenderedLocation, headlessCart, headlessCartInfo, dispatch, false, maxSelectableVariants);
          } else {
            dispatch({
              type: "SET_NOTIFICATION",
              payload: {
                title: `Too many sessions selected.`,
                content: `You have selected the maximum number of timeslots for your booking.`,
                isVisible: true,
                variant: {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              },
            });
          }
        } else if (trackSelectionOption.title === "VIRTUAL_REALITY") {
          if (isSelected) {
            dispatch({
              type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
              payload: {
                variantsAndDate: {
                  ...vrSelection.variantsAndDate,
                  selectedSessionTime: sessionDisplaytime,
                  selectedDate: new Date(formattedDateString),
                  selectedVariants: vrSelection.variantsAndDate.selectedVariants.filter((xv) => xv.variantsSelectedObject.id !== variant.id),
                },
              },
            });
            onClick();
            const cartData = await getCartData(productRenderedLocation, headlessCartInfo);

            let lineItems: any[] = [];
            if (cartData) {
              const { cart } = cartData;
              if (cart) {
                const { lines, items } = cart;
                if (items && items.length > 0) {
                  lineItems = items;
                }
                if (lines) {
                  const { edges } = lines;
                  if (edges) {
                    lineItems = edges.map((edge: any) => ({ ...edge.node.merchandise, headlessCartId: edge.node.id }));
                  }
                }
              }
            }

            const lineItem = lineItems.find((line) => variant.id.includes(line.id));

            if (lineItem) {
              await handleQuantityUpdate(productRenderedLocation === "external" ? lineItem.headlessCartId : lineItem.id, 0);
            }
          } else if (selectedVariants.length + 1 <= maxSelectableVariants) {
            const addSelectedVariants = {
              ...vrSelection.variantsAndDate,
              selectedSessionTime: sessionDisplaytime,
              selectedDate: new Date(formattedDateString),
              selectedVariants: [
                ...vrSelection.variantsAndDate.selectedVariants,
                {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              ],
            };
            dispatch({
              type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
              payload: {
                variantsAndDate: addSelectedVariants,
              },
            });
            onClick();
            await handleAddToCartClick([cartPayload], currentQuantity, productRenderedLocation, headlessCart, headlessCartInfo, dispatch, false, maxSelectableVariants);
          } else {
            dispatch({
              type: "SET_NOTIFICATION",
              payload: {
                title: `Too many sessions selected.`,
                content: `You have selected the maximum number of timeslots for your booking.`,
                isVisible: true,
                variant: {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              },
            });
          }
        } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
          if (isSelected) {
            dispatch({
              type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
              payload: {
                variantsAndDate: {
                  ...battleKartSelection.variantsAndDate,
                  selectedSessionTime: sessionDisplaytime,
                  selectedDate: new Date(formattedDateString),
                  selectedVariants: battleKartSelection.variantsAndDate.selectedVariants.filter((xv) => xv.variantsSelectedObject.id !== variant.id),
                },
              },
            });
            onClick();
            const cartData = await getCartData(productRenderedLocation, headlessCartInfo);

            let lineItems: any[] = [];
            if (cartData) {
              const { cart } = cartData;
              if (cart) {
                const { lines, items } = cart;
                if (items && items.length > 0) {
                  lineItems = items;
                }
                if (lines) {
                  const { edges } = lines;
                  if (edges) {
                    lineItems = edges.map((edge: any) => ({ ...edge.node.merchandise, headlessCartId: edge.node.id }));
                  }
                }
              }
            }

            const lineItem = lineItems.find((line) => variant.id.includes(line.id));

            if (lineItem) {
              await handleQuantityUpdate(productRenderedLocation === "external" ? lineItem.headlessCartId : lineItem.id, 0);
            }
          } else if (selectedVariants.length + 1 <= maxSelectableVariants) {
            const addSelectedVariants = {
              ...battleKartSelection.variantsAndDate,
              selectedSessionTime: sessionDisplaytime,
              selectedDate: new Date(formattedDateString),
              selectedVariants: [
                ...battleKartSelection.variantsAndDate.selectedVariants,
                {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              ],
            };
            dispatch({
              type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
              payload: {
                variantsAndDate: addSelectedVariants,
              },
            });
            onClick();
            await handleAddToCartClick([cartPayload], currentQuantity, productRenderedLocation, headlessCart, headlessCartInfo, dispatch, false, maxSelectableVariants);
          } else {
            dispatch({
              type: "SET_NOTIFICATION",
              payload: {
                title: `Too many sessions selected.`,
                content: `You have selected the maximum number of timeslots for your booking.`,
                isVisible: true,
                variant: {
                  variantSelectedTime: new Date(formattedDateString),
                  variantsSelectedObject: variant,
                },
              },
            });
          }
        }
      }
    }
  };

  const handleTouchEnd = async (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Convert touch event to mouse event and call handleClick
    const mouseEvent = {
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    } as React.MouseEvent<HTMLDivElement>;

    await handleClick(mouseEvent);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (warningMessage) {
        setWarningMessage(null);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [warningMessage]);

  // Battle Kart specific layout
  if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
    const getGameModeIcon = (gameMode: string) => {
      const cleanedGameMode = gameMode.trim().toLowerCase();

      if (cleanedGameMode.includes("battlerace") || cleanedGameMode.includes("race")) {
        return <BattleRaceIcon />;
      } else if (cleanedGameMode.includes("battlesnake") || cleanedGameMode.includes("snake")) {
        return <BattleSnakeIcon />;
      } else if (cleanedGameMode.includes("battlecolour") || cleanedGameMode.includes("battlecolor") || cleanedGameMode.includes("colour") || cleanedGameMode.includes("color")) {
        return <BattleColourIcon />;
      } else if (cleanedGameMode.includes("battlefoot") || cleanedGameMode.includes("foot")) {
        return <BattleKartFootSVGIcon />;
      } else {
        return <BattleRaceIcon />;
      }
    };

    let gameMode1 = "";
    let gameMode2 = "";

    if (variant.title.includes(" / ")) {
      const parts = variant.title.split(" / ");

      if (parts.length >= 3) {
        // Format: "TIME / GAME_MODE_1 / GAME_MODE_2"
        gameMode1 = parts[1]?.trim() || "";
        gameMode2 = parts[2]?.trim() || "";
      } else if (parts.length === 2) {
        const firstPart = parts[0]?.trim().toLowerCase();
        if (firstPart.includes("pm") || firstPart.includes("am") || firstPart.includes(":")) {
          gameMode1 = parts[1]?.trim() || "";
          gameMode2 = parts[1]?.trim() || "";
        } else {
          // Both parts are game modes
          gameMode1 = parts[0]?.trim() || "";
          gameMode2 = parts[1]?.trim() || "";
        }
      }
    } else {
      gameMode1 = variant.title.trim();
      gameMode2 = variant.title.trim();
    }

    return (
      <div
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        className={`tw-rounded-[6px] tw-overflow-hidden tw-cursor-pointer tw-flex tw-flex-col tw-relative tw-z-[39] tw-m-[2px] tw-font-medium tw-min-h-[50px] ${
          isSelected && isAvailable ? "tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A]" : isAvailable ? "tw-bg-white" : "tw-opacity-50 tw-bg-white"
        }`}
      >
        {/* Time section */}
        <div className="tw-pt-[2px] tw-px-[4px] tw-text-center">
          <div className={`tw-text-[16px] tw-leading-tight tw-font-medium ${isSelected && isAvailable ? "tw-text-white" : "tw-text-black"}`}>{getRaceTimeForVariant(variant)}</div>
        </div>

        {/* Game modes section below time */}
        <div className="tw-pb-[2px] tw-px-[4px] tw-flex tw-items-center tw-justify-center tw-gap-[4px]">
          <div className="tw-w-[22px] tw-h-[22px] tw-rounded-[4px] tw-overflow-hidden">{getGameModeIcon(gameMode1)}</div>
          <div className="tw-w-[22px] tw-h-[22px] tw-rounded-[4px] tw-overflow-hidden">{getGameModeIcon(gameMode2)}</div>
        </div>

        {/* Bottom section with price and seats in straight line */}
        <div className="tw-flex tw-h-[18px]">
          {/* Price in blue - smaller width */}
          <div className={`${handlePriceColor(variant.priceV2.amount)} tw-w-[30%] tw-py-[1px] tw-px-[1px] tw-text-center tw-flex tw-items-center tw-justify-center`}>
            <div className="tw-text-white tw-text-[12px]">${Number(variant.priceV2.amount)}</div>
          </div>

          {/* Seats left in black - larger width */}
          <div className="tw-bg-black tw-w-[70%] tw-py-[1px] tw-px-[1px] tw-text-left tw-flex tw-items-center tw-justify-start tw-pl-[8px]">
            <div className="tw-text-white tw-text-[10px] md:tw-text-[11px] tw-font-bold">{leftSeats || 0} left</div>
          </div>
        </div>
      </div>
    );
  }

  return !isAvailable && variant.availableForSale ? (
    <Tooltip tooltipText="Seats required exceeds available">
      {warningMessage && <Notifier message={warningMessage} />}

      <div
        className={`tw-bg-[#c8c8c8] tw-rounded-[5px] tw-overflow-hidden tw-text-center tw-cursor-pointer tw-flex tw-flex-col tw-justify-center tw-relative tw-z-[39] tw-m-[2px] tw-font-medium`}
        onMouseEnter={() => setWarningMessage("Seats required exceeds available")}
        onClick={() => setWarningMessage("Seats required exceeds available")}
      >
        <div className={`tw-text-[#494949] tw-text-[18px] time tw-relative tw-z-[99]`}>
          {trackSelectionOption.title === "MAIN_TRACK" || trackSelectionOption.title === "MINI_TRACK" || trackSelectionOption.title === "JUNIOR_TRACK"
            ? getRaceTimeForVariant(variant)
            : getGameTimeForVariant(variant)}
        </div>
        <div className="tw-flex tw-flex-col sm:tw-flex-row md:tw-flex-row tw-pb-[8px] sm:tw-pb-[0px] tw-items-center tw-bg-black">
          <div
            className={`tw-text-[12px] tw-w-100 tw-font-bold tw-bg-[#7e7e7e] tw-text-[#b4b4b4] tw-mt-auto tw-relative tw-z-[99] tw-w-full md:tw-w-fit tw-px-[4px] tw-mb-[1px] sm:tw-mb-[0px] tw-whitespace-nowrap variantId${getAdminIdFromStoreFrontId(
              variant.id
            )}`}
          >
            ${Number(variant.priceV2.amount)}
          </div>

          <div className="tw-text-[10px] tw-pt-[8px] sm:tw-pt-[0px] md:tw-text-[12px] tw-leading-[0] tw-text-[#b4b4b4] tw-w-full tw-z-[5]">
            <span className="tw-font-bold tw-mr-0.5">{leftSeats || 0}</span> {trackSelectionOption.title === "VIRTUAL_REALITY" ? "left" : "left"}
          </div>
        </div>
      </div>
    </Tooltip>
  ) : (
    <div
      onClick={handleClick}
      onTouchEnd={handleTouchEnd}
      className={`tw-rounded-[5px] tw-overflow-hidden tw-text-center tw-cursor-pointer tw-flex tw-flex-col tw-justify-center tw-relative tw-z-[39] tw-m-[2px] tw-font-medium ${
        isSelected && isAvailable
          ? "tw-text-white border-color"
          : isAvailable
          ? "tw-bg-white tw-justify-between tw-min-h-[44px]"
          : "tw-bg-[#777] tw-justify-center tw-text-[#393B41] tw-min-h-[58px] sm:tw-min-h-[44px]"
      }`}
    >
      <div
        className={`tw-text-[15px] md:tw-text-[18px] time tw-relative tw-z-[99] ${
          isSelected && isAvailable ? "tw-text-white border-color" : isAvailable ? "tw-text-[#393B41]" : ""
        }`}
      >
        {trackSelectionOption.title === "MAIN_TRACK" || trackSelectionOption.title === "MINI_TRACK" || trackSelectionOption.title === "JUNIOR_TRACK"
          ? getRaceTimeForVariant(variant)
          : getGameTimeForVariant(variant)}
      </div>
      {isAvailable && (
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-pb-[8px] sm:tw-pb-[0px] tw-items-center tw-bg-black">
          <div
            className={`${handlePriceColor(
              variant.priceV2.amount
            )} tw-text-[10px] tw-w-100 tw-font-bold tw-text-white tw-mt-auto tw-relative tw-z-[99] tw-w-full md:tw-w-fit tw-px-[4px] tw-mb-[1px] sm:tw-mb-[0px] tw-whitespace-nowrap tw-w-full sm:tw-w-auto ${
              isActiveRace && isSelected && isAvailable ? "tw-bg-[#393b4180]" : ""
            } variantId${getAdminIdFromStoreFrontId(variant.id)} ${!isAvailable ? "tw-hidden" : ""}`}
          >
            ${Number(variant.priceV2.amount)}
          </div>

          <div className="tw-text-[10px] tw-pt-[8px] max-[380px]:tw-text-[9px] sm:tw-pt-[0px] md:tw-text-[10.5px] tw-leading-[0] tw-text-white tw-w-full tw-z-[5]">
            <span className="tw-font-bold tw-mr-0.5">{leftSeats || 0}</span> {trackSelectionOption.title === "VIRTUAL_REALITY" ? "left" : "left"}
          </div>
        </div>
      )}
    </div>
  );
}
